import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, ArrowRight } from "lucide-react";
import FadeIn from "@/components/FadeIn";
import { scrollToId } from "@/lib/scroll";
import { DEPARTEMENTS, MAP_VIEWBOX } from "@/lib/nouvelleAquitaine";

const ZONES = [
  { code: "33", name: "Gironde" },
  { code: "16", name: "Charente" },
  { code: "17", name: "Charente-Maritime" },
  { code: "24", name: "Dordogne" },
  { code: "47", name: "Lot-et-Garonne" },
  { code: "40", name: "Landes" },
];
const ZONE_CODES = new Set(ZONES.map((z) => z.code));
const LABEL_OFFSET = { 17: [22, 18], 33: [8, 6], 16: [4, 8] };
const OFFICE = { x: 206.3, y: 356.9 };

const Dept = ({ d, active, hovered, onHover }) => (
  <motion.path
    data-testid={`zone-map-dept-${d.code}`}
    d={d.d}
    onMouseEnter={() => active && onHover(d.code)}
    onMouseLeave={() => onHover(null)}
    initial={false}
    animate={{
      fill: !active ? "#E2E8E4" : hovered ? "#34D399" : "#064E3B",
    }}
    transition={{ duration: 0.3 }}
    stroke="#F8FAF8"
    strokeWidth={2.2}
    strokeLinejoin="round"
    className={active ? "cursor-pointer" : ""}
  />
);

const Label = ({ d, hovered }) => {
  const [ox, oy] = LABEL_OFFSET[d.code] || [0, 0];
  return (
    <g pointerEvents="none" transform={`translate(${d.cx + ox} ${d.cy + oy})`}>
      <text
        textAnchor="middle"
        className="font-display"
        fontSize={hovered ? 22 : 19}
        fontWeight={800}
        fill="#FFFFFF"
        style={{ transition: "font-size .25s" }}
      >
        {d.code}
      </text>
    </g>
  );
};

export default function Zones() {
  const [hovered, setHovered] = useState(null);

  return (
    <section
      id="zones"
      data-testid="section-zones"
      className="relative overflow-hidden border-b border-slate-200 bg-white py-24 text-ink sm:py-32"
    >
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1.1fr] lg:gap-20">
        <div>
          <FadeIn>
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-emerald-700 sm:text-xs">
              Zones d'intervention
            </p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-5xl">
              Un installateur du Sud-Ouest, chez vous.
            </h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-600">
              Nos propres techniciens, sans sous-traitance, interviennent dans
              six départements depuis notre bureau de Saint-André-de-Cubzac.
              Visite technique, pose et SAV réactif, partout sur la zone.
            </p>
          </FadeIn>

          <FadeIn delay={0.1}>
            <ul className="mt-10 grid gap-2.5 sm:grid-cols-2" data-testid="zone-list">
              {ZONES.map((z, i) => {
                const on = hovered === z.code;
                return (
                  <li key={z.code}>
                    <button
                      type="button"
                      data-testid={`zone-item-${z.code}`}
                      onMouseEnter={() => setHovered(z.code)}
                      onMouseLeave={() => setHovered(null)}
                      onFocus={() => setHovered(z.code)}
                      onBlur={() => setHovered(null)}
                      className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-all duration-300 ${
                        on
                          ? "border-emerald-400 bg-emerald-400/10 shadow-glow"
                          : "border-slate-200 bg-white hover:border-emerald-300"
                      }`}
                    >
                      <span
                        className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg font-mono text-xs font-bold transition-colors ${
                          on ? "bg-emerald-400 text-forest-deep" : "bg-forest text-white"
                        }`}
                      >
                        {z.code}
                      </span>
                      <span className="font-display text-sm font-bold sm:text-base">
                        {z.name}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </FadeIn>

          <FadeIn delay={0.15}>
            <div className="mt-8 flex flex-col gap-5 sm:flex-row sm:items-center">
              <p className="flex items-center gap-2 text-sm text-slate-500">
                <MapPin className="h-4 w-4 text-emerald-600" />
                3 rue de la Dauge, 33240 Saint-André-de-Cubzac
              </p>
              <button
                data-testid="zones-cta-simulator"
                onClick={() => scrollToId("#simulateur")}
                className="group inline-flex w-fit items-center gap-2 rounded-full bg-forest px-6 py-3 text-sm font-bold text-white transition-all hover:scale-[1.03] hover:bg-emerald-700"
              >
                Vérifier mon éligibilité
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
            </div>
          </FadeIn>
        </div>

        <FadeIn delay={0.1} className="relative">
          <div className="pointer-events-none absolute inset-0 -z-10 rounded-full bg-emerald-400/10 blur-[110px]" />
          <svg
            data-testid="zone-map"
            viewBox={MAP_VIEWBOX}
            role="img"
            aria-label="Carte de la Nouvelle-Aquitaine : Gironde, Charente, Charente-Maritime, Dordogne, Lot-et-Garonne et Landes en couleur"
            className="mx-auto w-full max-w-[560px] drop-shadow-[0_20px_40px_rgba(6,78,59,0.15)]"
          >
            {DEPARTEMENTS.map((d) => (
              <Dept
                key={d.code}
                d={d}
                active={ZONE_CODES.has(d.code)}
                hovered={hovered === d.code}
                onHover={setHovered}
              />
            ))}
            {DEPARTEMENTS.filter((d) => ZONE_CODES.has(d.code)).map((d) => (
              <Label key={d.code} d={d} hovered={hovered === d.code} />
            ))}
            <g data-testid="zone-map-office" transform={`translate(${OFFICE.x} ${OFFICE.y})`}>
              <circle r="16" fill="#34D399" opacity="0.35">
                <animate attributeName="r" values="10;22;10" dur="2.4s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.45;0;0.45" dur="2.4s" repeatCount="indefinite" />
              </circle>
              <circle r="6" fill="#34D399" stroke="#FFFFFF" strokeWidth="2.5" />
            </g>
          </svg>
          <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
            <span className="mr-2 inline-block h-2 w-2 rounded-full bg-emerald-400 align-middle" />
            Notre bureau · Saint-André-de-Cubzac
          </p>
        </FadeIn>
      </div>
    </section>
  );
}
